import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ProviderUser {
  mbrNo: string
  mbrId: string
  mbrNm: string
  orgNm?: string
  mngrNm?: string
  mbrSeCd?: string
  userTypeDtlCd?: string
  email?: string
  phone?: string
  repPhone?: string
}

interface ProviderAuthState {
  user: ProviderUser | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loginTime: number | null
}

const storedUser = localStorage.getItem('provider_user')
const storedAccessToken = localStorage.getItem('provider_accessToken')
const storedRefreshToken = localStorage.getItem('provider_refreshToken')
const storedLoginTime = localStorage.getItem('provider_loginTime')

const initialState: ProviderAuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  accessToken: storedAccessToken || null,
  refreshToken: storedRefreshToken || null,
  isAuthenticated: !!storedAccessToken,
  loginTime: storedLoginTime ? parseInt(storedLoginTime, 10) : null,
}

const providerAuthSlice = createSlice({
  name: 'providerAuth',
  initialState,
  reducers: {
    setProviderCredentials: (
      state,
      action: PayloadAction<{
        user: ProviderUser
        accessToken: string
        refreshToken: string
      }>
    ) => {
      const { user, accessToken, refreshToken } = action.payload
      const loginTime = Date.now()
      state.user = user
      state.accessToken = accessToken
      state.refreshToken = refreshToken
      state.isAuthenticated = true
      state.loginTime = loginTime

      localStorage.setItem('provider_user', JSON.stringify(user))
      localStorage.setItem('provider_accessToken', accessToken)
      localStorage.setItem('provider_refreshToken', refreshToken)
      localStorage.setItem('provider_loginTime', String(loginTime))
    },
    updateProviderAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload
      localStorage.setItem('provider_accessToken', action.payload)
    },
    providerLogout: (state) => {
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.loginTime = null

      localStorage.removeItem('provider_user')
      localStorage.removeItem('provider_accessToken')
      localStorage.removeItem('provider_refreshToken')
      localStorage.removeItem('provider_loginTime')
    },
    updateProviderUser: (state, action: PayloadAction<Partial<ProviderUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('provider_user', JSON.stringify(state.user))
      }
    },
  },
})

export const { setProviderCredentials, updateProviderAccessToken, providerLogout, updateProviderUser } = providerAuthSlice.actions

export const selectProviderUser = (state: { providerAuth: ProviderAuthState }) => state.providerAuth.user
export const selectProviderIsAuthenticated = (state: { providerAuth: ProviderAuthState }) => state.providerAuth.isAuthenticated
export const selectProviderAccessToken = (state: { providerAuth: ProviderAuthState }) => state.providerAuth.accessToken
export const selectProviderRefreshToken = (state: { providerAuth: ProviderAuthState }) => state.providerAuth.refreshToken
export const selectProviderLoginTime = (state: { providerAuth: ProviderAuthState }) => state.providerAuth.loginTime

export default providerAuthSlice.reducer
